import { useIsFeatureEnabled } from '@repo/crm-atoms';
import { FeatureConfigurationFeatureName } from '@repo/utils';
import { CaseUtil } from '@vectord/utils';
import type { MenuProps } from 'antd';
import { Menu, Modal } from 'antd';
import { NodeType } from 'automations/data/nodeTypes';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import { useConfig } from 'automations/hooks/useConfig';
import { replaceFallbackNode } from 'automations/store/reactFlowSlice';
import { Triggers, type TriggersConfig } from 'automations/types/automationConfig';
import { generateNewNodePayload } from 'automations/utils/node';
import { useCallback, useMemo } from 'react';

export default function CreateTriggerMenu({
  nodeId,
  open,
  setOpen,
  afterCreate,
}: {
  nodeId: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  afterCreate?: () => void;
}) {
  const dispatch = useAutomationDispatch();
  const { triggers } = useConfig();
  const isDealsEnabled = useIsFeatureEnabled(FeatureConfigurationFeatureName.Deals);

  const handleTriggerSelect = useCallback((trigger: TriggersConfig) => {
    const newNode = generateNewNodePayload({
      nodeType: NodeType.trigger,
      data: {
        nodeSubType: trigger.name,
        menuOpen: true,
      },
    });
    dispatch(replaceFallbackNode({ nodeId, newNode }));
    afterCreate?.();
    setOpen(false);
  }, [afterCreate, dispatch, nodeId, setOpen]);
  const menuItems: MenuProps['items'] = useMemo(() => {
    const groups: { [key: string]: TriggersConfig[] } = {};

    triggers?.forEach((trigger) => {
      // show items that are configured in FE config.
      if (!Object.values(Triggers).includes(trigger.name as Triggers)) return;
      if (
        !isDealsEnabled
        && (trigger.name === Triggers.dealCreate || trigger.name === Triggers.dealUpdate)
      ) {
        return;
      }
      (groups[trigger.group_name] ??= []).push(trigger);
    });

    return Object.entries(groups).map(([groupName, groupTriggers]) => ({
      key: groupName,
      label: groupName,
      children: groupTriggers.map((trigger) => ({
        key: trigger.name,
        label: CaseUtil.titleCase(trigger.name),
        onClick: () => handleTriggerSelect(trigger),
      })),
    }));
  }, [triggers, handleTriggerSelect, isDealsEnabled]);

  return (
    <Modal
      open={open}
      centered
      onCancel={() => setOpen(false)}
      title="Add Trigger Node"
      footer={null}
    >
      <Menu mode="inline" items={menuItems} />
    </Modal>
  );
}
